import { TURN_VARIABLES, type TurnVariableDef, type TurnVariableGroup } from "@swivl-mono/types";
import { useEffect, useRef, useState } from "react";

interface TurnVariablePickerProps {
  /** Called with the full template token (e.g. `"{{ZipCodeOfValidLocation}}"`)
   *  when the user selects a variable. The caller is responsible for inserting
   *  it into its textarea / input at the cursor. */
  onInsert: (token: string) => void;
  /** Optional label for the trigger button. */
  triggerLabel?: string;
}

const GROUP_ORDER: TurnVariableGroup[] = ["Location", "User", "Negative"];

/**
 * Compact dropdown that lets authors browse the canonical `TURN_VARIABLES`
 * registry and insert a `{{name}}` token into a turn's content (or a criterion
 * check, judgeContext, etc.). Reads the registry directly so adding a new
 * variable in libs/types automatically shows up here.
 */
export function TurnVariablePicker({ onInsert, triggerLabel = "+ Insert variable" }: TurnVariablePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const grouped = new Map<TurnVariableGroup, TurnVariableDef[]>();
  for (const v of TURN_VARIABLES) {
    const arr = grouped.get(v.group) ?? [];
    arr.push(v);
    grouped.set(v.group, arr);
  }

  const handlePick = (v: TurnVariableDef): void => {
    onInsert(`{{${v.name}}}`);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-orange-300 bg-white px-2 py-0.5 text-xs text-orange-600 hover:bg-orange-50"
      >
        {triggerLabel}
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 max-h-80 w-80 overflow-y-auto rounded border border-grey-200 bg-white shadow-lg">
          {GROUP_ORDER.filter((g) => grouped.has(g)).map((group, i) => (
            <div key={group} className={i > 0 ? "border-t border-grey-100" : ""}>
              <div className="bg-grey-50 px-2 py-1 text-[11px] font-semibold tracking-wider text-grey-500 uppercase">{group}</div>
              {(grouped.get(group) ?? []).map((v) => (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => handlePick(v)}
                  className="flex w-full flex-col items-start gap-0.5 border-b border-grey-50 px-2 py-1.5 text-left last:border-b-0 hover:bg-orange-50"
                >
                  <code className="font-mono text-xs text-orange-700">{`{{${v.name}}}`}</code>
                  <span className="text-[11px] text-grey-500">{v.label}</span>
                  <span className="text-[10px] text-grey-400">{v.description}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Helper for callers: insert `token` at the current caret position of the
 * given textarea/input element, then refocus + place caret after the inserted
 * token. Falls back to appending if the ref or selection isn't available.
 */
export function insertAtCaret(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  token: string,
  currentValue: string,
  onChange: (next: string) => void
): void {
  if (!el) {
    onChange(currentValue + token);
    return;
  }
  const start = el.selectionStart ?? currentValue.length;
  const end = el.selectionEnd ?? currentValue.length;
  const next = currentValue.slice(0, start) + token + currentValue.slice(end);
  onChange(next);
  // After React updates the value, restore caret to just past the inserted token.
  requestAnimationFrame(() => {
    el.focus();
    const caret = start + token.length;
    el.setSelectionRange(caret, caret);
  });
}
